import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Input,
    Button as AntButton,
    message,
    Modal,
    Typography,
    Spin,
    Tag
} from 'antd';
import styled from 'styled-components';
import {
    createJobExtensionField,
    updateJobExtensionField,
    deleteJobExtensionField,
    getJob
} from '../../../api/jobs';
import { getCompanyDefaultJobExtensionFieldsForCompany } from '../../../api/company';
import { JobExtensionField, CompanyDefaultJobExtensionField, Job as JobType } from '../../../types';
import {
    ExclamationCircleOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined
} from '@ant-design/icons';
import Button from '../../../UI/buttons/Button';
import { JOBS } from '../../../constants/routes';
import { useHistoryWithUrlBase } from '../../../hooks/useHirePortContext';
import useHirePortTheme from '../../../hooks/useHirePortTheme';

const { confirm } = Modal;
const { Title: SectionTitle, Text: SectionDescription } = Typography;

const Container = styled.div`
  max-width: 100%;
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const FormTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  line-height: 1.25;
  text-align: left;
  color: #061d31;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 0.875rem;
  line-height: 1.71;
  color: #627d95;
  margin-bottom: 2rem;
`;

const FieldCard = styled.div`
  border-radius: 8px;
  margin-bottom: 1rem;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s;
  position: relative;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
  
  &.editing {
    background-color: #f9fafb;
    border: 1px solid #e8eef4;
  }
`;

const FieldName = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.71;
  color: #061c2e;
  flex: 1;
`;

const EditableInput = styled(Input)`
  flex: 1;
  margin-right: 16px;
  
  &.ant-input {
    border-radius: 4px;
    border: 1px solid #d9d9d9;
    padding: 4px 11px;
    
    &:hover, &:focus {
      border-color: ${({ theme }) => theme.primaryColor};
      box-shadow: none;
    }
  }
`;

const FieldActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(AntButton)`
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: #f9fafb;
  border: 1px dashed #aebeca;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: #aebeca;
  margin-bottom: 1rem;
`;

const FormActions = styled.div`
  padding: 1.5rem 2rem;
  border-radius: ${({ theme }) => theme.radius};
  background-color: ${({ theme }) => theme.lozengeColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
`;

const SmallAddButton = styled(AntButton)`
  font-size: 0.75rem;
  height: 32px;
  padding: 0 8px;
  
  &.ant-btn {
    border-radius: ${({ theme }) => theme.radius};
    border-color: ${({ theme }) => theme.primaryColor};
    color: ${({ theme }) => theme.primaryColor};
    background-color: transparent;
    box-shadow: none;
    
    &:hover {
      color: ${({ theme }) => theme.primaryColorLight || theme.hoverColor};
      border-color: ${({ theme }) => theme.primaryColorLight || theme.hoverColor};
    }
  }
`;

const StyledAddDefaultButton = styled(AntButton)`
  &.ant-btn {
    border-radius: ${({ theme }) => theme.radius};
  }
`;

const StyledInheritedTag = styled(Tag)`
  &.ant-tag {
    border-radius: ${({ theme }) => theme.radius};
  }
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid #e8eef4;
  margin: 2rem 0;
`;

const FieldNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

interface JobExtensionFieldsFormProps {
    jobId: string;
    onFinish: (jobId: string) => void;
}

const JobExtensionFieldsForm: React.FC<JobExtensionFieldsFormProps> = ({
    jobId,
    onFinish
}) => {
    const { t } = useTranslation();
    const history = useHistoryWithUrlBase();
    const theme = useHirePortTheme();
    const [jobSpecificFields, setJobSpecificFields] = useState<JobExtensionField[]>([]);
    const [companyDefaultFields, setCompanyDefaultFields] = useState<CompanyDefaultJobExtensionField[]>([]);
    const [currentJob, setCurrentJob] = useState<JobType | null>(null);
    const [loading, setLoading] = useState(false);
    const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState('');
    const [addingNewField, setAddingNewField] = useState(false);
    const [newFieldValue, setNewFieldValue] = useState('');

    const fetchData = useCallback(async () => {
        if (!jobId) return;
        setLoading(true);
        try {
            const jobDetailsResponse = await getJob(jobId, {} as any);
            const fetchedJob = jobDetailsResponse.data;
            setCurrentJob(fetchedJob);
            setJobSpecificFields(fetchedJob.extension_fields || []);

            if (fetchedJob.company && fetchedJob.company.id) {
                const defaultsResponse = await getCompanyDefaultJobExtensionFieldsForCompany(fetchedJob.company.id);
                setCompanyDefaultFields(defaultsResponse.data);
            } else {
                setCompanyDefaultFields([]);
            }
        } catch (error) {
            console.error('Error fetching extension fields data', error);
            message.error(t('ERROR_FETCHING_DATA', 'Failed to load extension field data.'));
        } finally {
            setLoading(false);
        }
    }, [jobId, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const startEditing = (field: JobExtensionField) => {
        setEditingFieldId(field.id);
        setEditingValue(field.name);
    };

    const cancelEditing = () => {
        setEditingFieldId(null);
        setEditingValue('');
    };

    const saveEdit = async () => {
        if (!editingFieldId || !editingValue.trim() || !currentJob) {
            return;
        }
        try {
            await updateJobExtensionField(currentJob.id, editingFieldId, { name: editingValue });
            message.success(t('FIELD_UPDATED_SUCCESS', 'Field updated successfully'));
            setEditingFieldId(null);
            setEditingValue('');
            fetchData();
        } catch (error) {
            console.error('Error updating field', error);
            message.error(t('ERROR_UPDATING_FIELD', 'Error updating field'));
        }
    };

    const startAddingField = () => {
        setAddingNewField(true);
        setNewFieldValue('');
    };

    const cancelAddingField = () => {
        setAddingNewField(false);
        setNewFieldValue('');
    };

    const saveNewField = async () => {
        if (!newFieldValue.trim() || !currentJob) {
            return;
        }
        try {
            await createJobExtensionField(currentJob.id, {
                name: newFieldValue,
                is_numeric: false,
                is_currency: false,
                is_multiline: true,
            });
            message.success(t('FIELD_ADDED_SUCCESS', 'Field added successfully'));
            setAddingNewField(false);
            setNewFieldValue('');
            fetchData();
        } catch (error) {
            console.error('Error adding new field', error);
            message.error(t('ERROR_ADDING_FIELD', 'Error adding field'));
        }
    };

    const handleDelete = (field: JobExtensionField) => {
        if (!currentJob) return;
        confirm({
            title: t('CONFIRM_DELETE_FIELD_TITLE', 'Are you sure you want to delete this field?'),
            icon: <ExclamationCircleOutlined />,
            content: t('CONFIRM_DELETE_FIELD_CONTENT', 'This action cannot be undone.'),
            okText: t('YES', 'Yes'),
            okType: 'danger',
            cancelText: t('NO', 'No'),
            onOk: async () => {
                try {
                    await deleteJobExtensionField(currentJob.id, field.id);
                    message.success(t('FIELD_DELETED_SUCCESS', 'Field deleted successfully'));
                    fetchData();
                } catch (error) {
                    console.error('Error deleting field', error);
                    message.error(t('ERROR_DELETING_FIELD', 'Error deleting field'));
                }
            },
        });
    };

    const jobFieldNamesLower = useMemo(() => new Set(jobSpecificFields.map(f => f.name.toLowerCase())), [jobSpecificFields]);

    const availableCompanyDefaults = useMemo(() => {
        return companyDefaultFields.filter(
            defaultField => !jobFieldNamesLower.has(defaultField.name.toLowerCase())
        );
    }, [companyDefaultFields, jobFieldNamesLower]);

    const handleAddDefaultField = async (defaultField: CompanyDefaultJobExtensionField) => {
        if (!currentJob) return;
        const newJobFieldData = {
            name: defaultField.name,
            is_numeric: defaultField.is_numeric,
            is_currency: defaultField.is_currency,
            is_multiline: defaultField.is_multiline,
        };
        try {
            await createJobExtensionField(currentJob.id, newJobFieldData);
            message.success(t('FIELD_ADDED_FROM_DEFAULTS_SUCCESS', { fieldName: defaultField.name }));
            fetchData();
        } catch (error) {
            console.error('Error adding default field to job', error);
            message.error(t('ERROR_ADDING_DEFAULT_FIELD', { fieldName: defaultField.name }));
        }
    };

    const handleClickNext = () => {
        if (currentJob) {
            onFinish(currentJob.id);
        }
    }

    const handleSaveAndPreview = () => {
        if (currentJob && currentJob.id) {
            history.push(`${JOBS}/${currentJob.id}`);
            message.success(t('DRAFT_SAVED_SUCCESS', 'Draft saved successfully!'));
        } else {
            message.error(t('ERROR_NO_JOB_ID_PREVIEW', 'Cannot preview, job ID is missing.'));
        }
    };

    const renderJobSpecificFields = () => (
        <>
            {jobSpecificFields.map(field => (
                <FieldCard key={field.id} className={editingFieldId === field.id ? 'editing' : ''}>
                    {editingFieldId === field.id ? (
                        <EditableInput
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            autoFocus
                            onPressEnter={saveEdit}
                        />
                    ) : (
                        <FieldName>{field.name}</FieldName>
                    )}
                    <FieldActions>
                        {editingFieldId === field.id ? (
                            <>
                                <ActionButton type="text" icon={<CheckOutlined />} onClick={saveEdit} title={t('SAVE', 'Save')} />
                                <ActionButton type="text" icon={<CloseOutlined />} onClick={cancelEditing} title={t('CANCEL', 'Cancel')} />
                            </>
                        ) : (
                            <>
                                <ActionButton type="text" icon={<EditOutlined />} onClick={() => startEditing(field)} title={t('EDIT', 'Edit')} />
                                <ActionButton type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(field)} title={t('DELETE', 'Delete')} />
                            </>
                        )}
                    </FieldActions>
                </FieldCard>
            ))}
            {addingNewField ? (
                <FieldCard className="editing">
                    <EditableInput
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        placeholder={t('ENTER_FIELD_NAME_PLACEHOLDER', 'Enter field name')}
                        autoFocus
                        onPressEnter={saveNewField}
                    />
                    <FieldActions>
                        <ActionButton type="text" icon={<CheckOutlined />} onClick={saveNewField} title={t('SAVE', 'Save')} />
                        <ActionButton type="text" icon={<CloseOutlined />} onClick={cancelAddingField} title={t('CANCEL', 'Cancel')} />
                    </FieldActions>
                </FieldCard>
            ) : (
                <div style={{ marginBottom: '1rem' }}>
                    <SmallAddButton icon={<PlusOutlined />} onClick={startAddingField}>
                        {t('ADD_NEW_CUSTOM_FIELD', 'Add new custom field')}
                    </SmallAddButton>
                </div>
            )}
        </>
    );

    const renderAvailableCompanyDefaults = () => (
        <>
            <SectionTitle level={4} style={{ marginTop: '2.5rem', marginBottom: '1rem' }}>
                {t('ADD_FROM_COMPANY_DEFAULTS_TITLE', 'Add from Company Questions')}
            </SectionTitle>
            <SectionDescription style={{ marginBottom: '1.5rem' }}>
                {t('ADD_FROM_COMPANY_DEFAULTS_DESC', 'You can add questions that are predefined for your company.')}
            </SectionDescription>
            {availableCompanyDefaults.length === 0 && (
                <EmptyStateWrapper>
                    <EmptyStateIcon><PlusOutlined /></EmptyStateIcon>
                    <p>{t('NO_AVAILABLE_COMPANY_DEFAULTS', 'No additional company questions available to add, or they are already added to this job.')}</p>
                </EmptyStateWrapper>
            )}
            {availableCompanyDefaults.map(defaultField => {
                const isInherited = currentJob && defaultField.company !== currentJob.company.id;
                return (
                    <FieldCard key={`default-${defaultField.id}`}>
                        <FieldNameContainer>
                            <FieldName>{defaultField.name}</FieldName>
                            {isInherited && <StyledInheritedTag color={theme.primaryColor}>{t('INHERITED_BADGE', 'Inherited')}</StyledInheritedTag>}
                        </FieldNameContainer>
                        <FieldActions>
                            <StyledAddDefaultButton
                                icon={<PlusOutlined />}
                                onClick={() => handleAddDefaultField(defaultField)}
                                size="small"
                            >
                                {t('ADD_FIELD_BUTTON', 'Add')}
                            </StyledAddDefaultButton>
                        </FieldActions>
                    </FieldCard>
                );
            })}
        </>
    );

    if (loading && !currentJob) {
        return <Spin size="large" style={{ display: 'block', marginTop: '50px' }} />;
    }

    return (
        <Container>
            <div>
                <PageHeader>
                    <FormTitle>{t('JOB_EXTENSION_FIELDS_TITLE', 'Additional Job Questions')}</FormTitle>
                    <Description>
                        {t('JOB_EXTENSION_FIELDS_DESC', 'Define custom questions specific to this job. Recruiters will be asked to provide answers for these.')}
                    </Description>
                </PageHeader>

                {jobSpecificFields.length === 0 && !addingNewField && availableCompanyDefaults.length === 0 ? (
                    <EmptyStateWrapper>
                        <EmptyStateIcon><PlusOutlined /></EmptyStateIcon>
                        <p>{t('NO_JOB_EXTENSION_FIELDS_YET', 'No additional questions defined for this job yet. Add a custom field or select from company defaults below (if available).')}</p>
                        <SmallAddButton icon={<PlusOutlined />} onClick={startAddingField} style={{ marginTop: '1rem' }}>
                            {t('ADD_NEW_CUSTOM_FIELD', 'Add new custom field')}
                        </SmallAddButton>
                    </EmptyStateWrapper>
                ) : (
                    renderJobSpecificFields()
                )}

                <SectionDivider />
                {renderAvailableCompanyDefaults()}
            </div>

            <FormActions>
                <Button
                    type="ghost"
                    onClick={handleSaveAndPreview}
                    size="large"
                    disabled={!currentJob}
                >
                    {t('SAVE_AND_PREVIEW_DRAFT_TITLE', 'Save and Preview Draft')}
                </Button>
                <Button
                    type="primary"
                    onClick={handleClickNext}
                    size="large"
                >
                    {t('NEXT_TITLE', 'Next')}
                </Button>
            </FormActions>
        </Container>
    );
};

export default JobExtensionFieldsForm; 